import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { ViewIcon } from "../../assets/icons/ViewIcon";
import { EditIcon } from "../../assets/icons/EditIcon";
import { DeleteIcon } from "../../assets/icons/DeleteIcon";
import { formatDateTimeAsStored } from "../../utils/utils";
// Shared form/modal styles (same .note-form grid + .mn-form-actions footer
// used by the Notes manage modal — keeps the look consistent).
import "../notes/ManageNotesModal.css";
import "./ReportForm.css";

const PORT = process.env.REACT_APP_PORT;
const ExcelReports = () => {
  const [excelReports, setExcelReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editingIndex, setEditingIndex] = useState(null); // Track which uploaded row is being edited
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentDetailIndex, setCurrentDetailIndex] = useState(0);
  const [isEditingInDetails, setIsEditingInDetails] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    loan: "",
    loanParent: "",
    note: "",
    control: "",
    balance: "",
    balanceDescription: "",
    holder: "",
    holderParent: "",
    holderCIK: "",
    depositor: "",
    depositorCIK: "",
    deal: "",
    dealCIK: "",
    accessionNumber: "",
    primaryDocument: "",
    filingDate: "",
    documentDate: "",
    closingDate: "",
    acceptanceDate: "",
  });

  // Fetch Excel Reports
  const fetchExcelReports = async (
    page = 1,
    search = "",
    limit = itemsPerPage
  ) => {
    setLoading(true);
    try {
      const response = await axios.get(`${PORT}getAllExcelReports`, {
        params: {
          page,
          limit,
          search,
        },
      });

      if (response.data.success) {
        setExcelReports(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setCurrentPage(response.data.pagination.currentPage);
        // Clear selections when data changes
        setSelectedReports([]);
      }
    } catch (error) {
      console.error("Error fetching Excel reports:", error);
      alert("Error fetching Excel reports");
    } finally {
      setLoading(false);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    fetchExcelReports(1, searchTerm, newItemsPerPage);
  };

  // Handle individual checkbox selection
  const handleCheckboxChange = (reportId) => {
    setSelectedReports((prev) => {
      if (prev.includes(reportId)) {
        return prev.filter((id) => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allReportIds = excelReports.map((report) => report.id);
      setSelectedReports(allReportIds);
    } else {
      setSelectedReports([]);
    }
  };

  // Check if all reports on current page are selected
  const isAllSelected =
    excelReports.length > 0 && selectedReports.length === excelReports.length;

  // Action button handler (for now just an alert)
  const handleActionButtonClick = async () => {
    if (selectedReports.length === 0) {
      alert("Please select at least one note to process.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${PORT}push-to-tables`, {
        excelReportIds: selectedReports,
      });

      if (response.data.success) {
        const { processed, failed, total } = response.data.data;

        if (failed === 0) {
          alert(`Successfully sent all ${processed} notes for minting!`);
        } else {
          alert(
            `Processing completed: ${processed} successful, ${failed} failed out of ${total} total notes.`
          );
        }

        // Refresh the table data
        fetchExcelReports();

        // Clear selected reports
        setSelectedReports([]);
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Error pushing notes to tables:", error);

      if (error.response && error.response.data) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert("Error pushing notes to tables. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedReports.length === 0) {
      alert("Please select at least one report to delete.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedReports.length} selected report(s)? This action cannot be undone.`
      )
    ) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${PORT}bulk-delete-excel-reports`, {
        reportIds: selectedReports,
      });

      if (response.data.success) {
        alert(`Successfully deleted ${selectedReports.length} report(s)!`);

        // Refresh the table data
        fetchExcelReports();

        // Clear selected reports
        setSelectedReports([]);
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Error bulk deleting reports:", error);

      if (error.response && error.response.data) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert("Error deleting reports. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch single report by ID
  const fetchReportById = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${PORT}getExcelReportById/${id}`);
      if (response.data.success) {
        setSelectedReport(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      alert("Error fetching report details");
    } finally {
      setLoading(false);
    }
  };

  // Create new report
  const createReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${PORT}createExcelReport`, formData);
      if (response.data.success) {
        alert("Report created successfully!");
        setShowCreateModal(false);
        resetForm();
        fetchExcelReports();
      }
    } catch (error) {
      console.error("Error creating report:", error);
      alert("Error creating report");
    } finally {
      setLoading(false);
    }
  };

  // Update report
  const updateReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.put(
        `${PORT}updateExcelReport/${selectedReport.id}`,
        formData
      );
      if (response.data.success) {
        alert("Report updated successfully!");
        setShowEditModal(false);
        resetForm();
        fetchExcelReports();
        setSelectedReport(null);
      }
    } catch (error) {
      console.error("Error updating report:", error);
      alert("Error updating report");
    } finally {
      setLoading(false);
    }
  };

  // Delete report
  const deleteReport = async (id) => {
    if (window.confirm("Are you sure you want to delete this report?")) {
      setLoading(true);
      try {
        const response = await axios.delete(`${PORT}deleteExcelReport/${id}`);
        if (response.data.success) {
          alert("Report deleted successfully!");
          fetchExcelReports();
          if (selectedReport && selectedReport.id === id) {
            setSelectedReport(null);
          }
          // Remove from selected reports if it was selected
          setSelectedReports((prev) =>
            prev.filter((reportId) => reportId !== id)
          );
        }
      } catch (error) {
        console.error("Error deleting report:", error);
        alert("Error deleting report");
      } finally {
        setLoading(false);
      }
    }
  };

  const setView = (report) => {
    setSelectedReport(report);
    setShowViewModal(true);
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Extract date from filename (e.g. "2023-02-23 16-45-32 BANK 2023-BNK45")
    const extractDateFromFilename = (filename) => {
      if (!filename) return null;

      const patterns = [
        // Filename format: 2023-02-23 16-45-32 (space, then time with hyphens)
        /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2})-(\d{2})-(\d{2})/,
        // ISO format: 2024-12-11_14-30-00 or 2024-12-11-14-30-00
        /(\d{4})[-_](\d{2})[-_](\d{2})[-_](\d{2})[-_](\d{2})[-_](\d{2})/,
        // Compact format: 20241211_143000 or 20241211-143000
        /(\d{4})(\d{2})(\d{2})[-_](\d{2})(\d{2})(\d{2})/,
        // Date only: 2024-12-11 or 2024_12_11
        /(\d{4})[-_](\d{2})[-_](\d{2})/,
        // Compact date: 20241211
        /(\d{4})(\d{2})(\d{2})/,
      ];

      for (const pattern of patterns) {
        const match = filename.match(pattern);
        if (match) {
          const [
            ,
            year,
            month,
            day,
            hour = "00",
            minute = "00",
            second = "00",
          ] = match;
          try {
            const dateStr = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
            const testDate = new Date(dateStr);
            if (!isNaN(testDate.getTime())) {
              return dateStr;
            }
          } catch (e) {}
        }
      }
      return null;
    };

    const extractedDate = extractDateFromFilename(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
          cellDates: true,
        });

        // Helper function to parse Excel dates
        const parseExcelDate = (dateVal) => {
          if (!dateVal) return "";
          if (dateVal instanceof Date) return dateVal;
          if (typeof dateVal === "number") {
            // Handle Excel serial date
            return new Date((dateVal - 25569) * 86400 * 1000);
          }
          return new Date(dateVal);
        };

        // Transform the data to match our form structure
        const transformedData = jsonData.map((row, index) => ({
          id: index + 1, // Temporary ID for display
          loan: row.loan || row.Loan || "",
          loanParent: row.loanParent || row["Loan Parent"] || "",
          note: row.note || row.Note || "",
          control: row.control || row.Control || "",
          balance: row.balance || row.Balance || "",
          balanceDescription:
            row.balanceDescription || row["Balance Description"] || "",
          holder: row.holder || row.Holder || "",
          holderParent: row.holderParent || row["Holder Parent"] || "",
          holderCIK: row.holderCIK || row["Holder CIK"] || "",
          depositor: row.depositor || row.Depositor || "",
          depositorCIK: row.depositorCIK || row["Depositor CIK"] || "",
          deal: row.deal || row.Deal || "",
          dealCIK: row.dealCIK || row["Deal CIK"] || "",
          accessionNumber: row.accessionNumber || row["Accession Number"] || "",
          primaryDocument: row.primaryDocument || row["Primary Document"] || "",
          filingDate: parseExcelDate(row.filingDate),
          documentDate: parseExcelDate(row.documentDate),
          closingDate: parseExcelDate(row.closingDate),
          acceptanceDate: extractedDate, // Add extracted date from filename
          filename: file.name, // Store original filename
        }));
        setUploadedData(transformedData);
        setShowUploadModal(true);
      } catch (error) {
        console.error("Error parsing file:", error);
        alert("Error parsing file. Please check the format.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Submit uploaded data
  const submitUploadedData = async () => {
    setLoading(true);
    try {
      const filename = uploadedData[0]?.filename || "";
      const response = await axios.post(`${PORT}bulk-push-to-tables`, {
        excelReports: uploadedData,
        filename: filename,
      });

      if (response.data.success) {
        alert("All reports uploaded successfully!");
        setShowUploadModal(false);
        setUploadedData([]);
        fetchExcelReports();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error uploading reports:", error);
      alert("Error uploading some reports");
    } finally {
      setLoading(false);
    }
  };

  // Edit uploaded row - NEW: Store the index and open edit modal
  const editUploadedRow = (index) => {
    const rowData = uploadedData[index];
    setFormData(rowData);
    setEditingIndex(index);
    setShowEditModal(true);
  };

  // Update uploaded row after edit
  const updateUploadedRow = (e) => {
    e.preventDefault();
    if (editingIndex !== null) {
      const updatedData = [...uploadedData];
      updatedData[editingIndex] = formData;
      setUploadedData(updatedData);
    }
    setShowEditModal(false);
    setEditingIndex(null);
    resetForm();
  };

  // Delete uploaded row
  const deleteUploadedRow = (index) => {
    const newData = uploadedData.filter((_, i) => i !== index);
    setUploadedData(newData);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      loan: "",
      loanParent: "",
      note: "",
      control: "",
      balance: "",
      balanceDescription: "",
      holder: "",
      holderParent: "",
      holderCIK: "",
      depositor: "",
      depositorCIK: "",
      deal: "",
      dealCIK: "",
      accessionNumber: "",
      primaryDocument: "",
      filingDate: "",
      documentDate: "",
      closingDate: "",
      acceptanceDate: "",
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Initialize edit form for main table
  const initEditForm = (report) => {
    setSelectedReport(report);
    setFormData({
      loan: report.loan || "",
      loanParent: report.loanParent || "",
      note: report.note || "",
      control: report.control || "",
      balance: report.balance || "",
      balanceDescription: report.balanceDescription || "",
      holder: report.holder || "",
      holderParent: report.holderParent || "",
      holderCIK: report.holderCIK || "",
      depositor: report.depositor || "",
      depositorCIK: report.depositorCIK || "",
      deal: report.deal || "",
      dealCIK: report.dealCIK || "",
      accessionNumber: report.accessionNumber || "",
      primaryDocument: report.primaryDocument || "",
      filingDate: report.filingDate ? report.filingDate.split("T")[0] : "",
      documentDate: report.documentDate
        ? report.documentDate.split("T")[0]
        : "",
      closingDate: report.closingDate ? report.closingDate.split("T")[0] : "",
    });
    setShowEditModal(true);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    fetchExcelReports(pageNumber, searchTerm, itemsPerPage);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchExcelReports(1, searchTerm, itemsPerPage);
  };

  // Handle column sorting
  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortDirection(newDirection);
  };

  // Get sorted data
  const getSortedData = (data) => {
    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle dates
      if (sortField === "createdAt") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle numbers
      if (sortField === "id" || sortField === "balance") {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Handle strings
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Open details modal
  const openDetailsModal = (index) => {
    setCurrentDetailIndex(index);
    setIsEditingInDetails(false);
    setShowDetailsModal(true);
  };

  // Close details modal
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setIsEditingInDetails(false);
    resetForm();
  };

  // Navigate to next detail
  const handleNextDetail = () => {
    const sortedData = getSortedData(excelReports);
    if (currentDetailIndex < sortedData.length - 1) {
      setCurrentDetailIndex(currentDetailIndex + 1);
      setIsEditingInDetails(false);
    }
  };

  // Navigate to previous detail
  const handlePrevDetail = () => {
    if (currentDetailIndex > 0) {
      setCurrentDetailIndex(currentDetailIndex - 1);
      setIsEditingInDetails(false);
    }
  };

  // Handle delete from details modal
  const handleDeleteInDetails = async () => {
    const sortedData = getSortedData(excelReports);
    const report = sortedData[currentDetailIndex];

    if (window.confirm("Are you sure you want to delete this report?")) {
      setLoading(true);
      try {
        const response = await axios.delete(
          `${PORT}deleteExcelReport/${report.id}`
        );
        if (response.data.success) {
          alert("Report deleted successfully!");

          if (
            currentDetailIndex >= excelReports.length - 1 &&
            currentDetailIndex > 0
          ) {
            setCurrentDetailIndex(currentDetailIndex - 1);
          }

          await fetchExcelReports();

          if (excelReports.length <= 1) {
            closeDetailsModal();
          }
        }
      } catch (error) {
        console.error("Error deleting report:", error);
        alert("Error deleting report");
      } finally {
        setLoading(false);
      }
    }
  };

  // Toggle edit mode in details
  const toggleEditInDetails = () => {
    const sortedData = getSortedData(excelReports);
    const report = sortedData[currentDetailIndex];

    if (!isEditingInDetails) {
      setFormData({
        loan: report.loan || "",
        loanParent: report.loanParent || "",
        note: report.note || "",
        control: report.control || "",
        balance: report.balance || "",
        balanceDescription: report.balanceDescription || "",
        holder: report.holder || "",
        holderParent: report.holderParent || "",
        holderCIK: report.holderCIK || "",
        depositor: report.depositor || "",
        depositorCIK: report.depositorCIK || "",
        deal: report.deal || "",
        dealCIK: report.dealCIK || "",
        accessionNumber: report.accessionNumber || "",
        primaryDocument: report.primaryDocument || "",
        filingDate: report.filingDate ? report.filingDate.split("T")[0] : "",
        documentDate: report.documentDate
          ? report.documentDate.split("T")[0]
          : "",
        closingDate: report.closingDate ? report.closingDate.split("T")[0] : "",
      });
      setIsEditingInDetails(true);
    } else {
      setIsEditingInDetails(false);
      resetForm();
    }
  };

  // Save edit in details modal
  const saveEditInDetails = async (e) => {
    e.preventDefault();
    const sortedData = getSortedData(excelReports);
    const report = sortedData[currentDetailIndex];

    setLoading(true);
    try {
      const response = await axios.put(
        `${PORT}updateExcelReport/${report.id}`,
        formData
      );
      if (response.data.success) {
        alert("Report updated successfully!");
        setIsEditingInDetails(false);
        resetForm();
        await fetchExcelReports();
      }
    } catch (error) {
      console.error("Error updating report:", error);
      alert("Error updating report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExcelReports();
  }, []);

  return (
    <div className="main-page-content">
      {/* Header with buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Search bar */}
        <div>
          <form onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "10px",
                width: "400px",
                border: "1px solid #ccc",
                borderRadius: "15px",
                marginRight: "10px",
              }}
            />
            <button type="submit" className="page-button">
              Search
            </button>
          </form>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          {/* Entries per page selector */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <label
              htmlFor="entries-select"
              style={{
                fontSize: "14px",
                color: "#666",
              }}
            ></label>
            <select
              id="entries-select"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "white",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <button
            className="page-button"
            onClick={() => setShowCreateModal(true)}
          >
            New Report
          </button>
          <div>
            <input
              type="file"
              id="file-upload"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <button
              className="page-button"
              onClick={() => document.getElementById("file-upload").click()}
            >
              Upload Report
            </button>
          </div>
        </div>
      </div>

      {/* Entries per page selector and table header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <div></div> {/* Empty div for spacing */}
      </div>

      {/* Main table */}
      <div className="note-container">
        <table className="note-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  style={{ marginRight: "8px" }}
                />
              </th>
              <th
                onClick={() => handleSort("id")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                ID {sortField === "id" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th
                onClick={() => handleSort("loan")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Loan{" "}
                {sortField === "loan" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th>Holder</th>
              <th
                onClick={() => handleSort("note")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Note{" "}
                {sortField === "note" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th
                onClick={() => handleSort("control")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Control{" "}
                {sortField === "control" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th>Balance Description</th>
              <th
                onClick={() => handleSort("balance")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Balance{" "}
                {sortField === "balance" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th
                onClick={() => handleSort("createdAt")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Created{" "}
                {sortField === "createdAt" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            ) : excelReports.length > 0 ? (
              getSortedData(excelReports).map((report, index) => (
                <tr
                  key={report.id}
                  onClick={() => openDetailsModal(index)}
                  style={{
                    cursor: "pointer",
                    ":hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedReports.includes(report.id)}
                      onChange={() => handleCheckboxChange(report.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>{report.id}</td>
                  <td>{report.loan}</td>
                  <td>{report.holder}</td>
                  <td>{report.note}</td>
                  <td>{report.control}</td>
                  <td>{report.balanceDescription}</td>
                  <td>{report.balance}</td>
                  <td>
                    {formatDateTimeAsStored(report.acceptanceDate)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: "center" }}>
                  No reports available
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination with Action and Delete Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "20px",
          }}
        >
          <div style={{ flex: 1 }}></div> {/* Empty space on left */}
          <div
            className="pagination"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <button
              className="arrow-button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              &larr;
            </button>

            {(() => {
              const pages = [];
              let startPage = Math.max(1, currentPage - 2);
              let endPage = Math.min(totalPages, startPage + 4);

              if (endPage - startPage < 4) {
                startPage = Math.max(1, endPage - 4);
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`page-button ${
                      currentPage === i ? "active" : ""
                    }`}
                    disabled={loading}
                  >
                    {i}
                  </button>
                );
              }

              return pages;
            })()}

            <button
              className="arrow-button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
            >
              &rarr;
            </button>
          </div>
          {/* Action and Delete Buttons */}
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            {/* Bulk Delete Button */}
            <button
              className="page-button"
              style={{
                backgroundColor:
                  selectedReports.length === 0 ? "#ccc" : "#dc3545",
                borderColor: selectedReports.length === 0 ? "#ccc" : "#dc3545",
                opacity: selectedReports.length === 0 ? 0.5 : 1,
                cursor:
                  selectedReports.length === 0 ? "not-allowed" : "pointer",
                color: "white",
              }}
              disabled={selectedReports.length === 0 || loading}
              onClick={handleBulkDelete}
            >
              Delete ({selectedReports.length})
            </button>

            {/* Action Button */}
            <button
              className="page-button"
              disabled={selectedReports.length === 0 || loading}
              onClick={handleActionButtonClick}
              style={{
                opacity: selectedReports.length === 0 ? 0.5 : 1,
                cursor:
                  selectedReports.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              Action ({selectedReports.length})
            </button>
          </div>
        </div>
      </div>

      {/* Details Modal with Navigation */}
      {showDetailsModal && excelReports.length > 0 && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ zIndex: 1001 }}
          >
            <button className="close-button" onClick={closeDetailsModal}>
              ×
            </button>

            {(() => {
              const sortedData = getSortedData(excelReports);
              const report = sortedData[currentDetailIndex];
              if (!report) return null;

              return (
                <div className="details-container">
                  {/* Modal Header */}
                  <div className="modal-header">
                    <h3
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Entry Details
                      <span className="modal-subtitle">
                        ({currentDetailIndex + 1} of {sortedData.length})
                      </span>
                    </h3>
                  </div>

                  <div className="modal-body">
                    {/* Details Content */}
                    <div className="detail-section">
                      {isEditingInDetails ? (
                        <form
                          onSubmit={saveEditInDetails}
                          className="edit-form"
                        >
                          <div className="detail-grid">
                            {/* Read-only ID */}
                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                ID
                              </label>
                              <input
                                type="text"
                                value={report.id}
                                disabled
                                className="form-input disabled"
                                style={{
                                  width: "100%",
                                  padding: "8px",
                                  backgroundColor: "#f0f0f0",
                                }}
                              />
                            </div>

                            {/* Editable Fields */}
                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Loan
                              </label>
                              <input
                                name="loan"
                                value={formData.loan}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Loan Parent
                              </label>
                              <input
                                name="loanParent"
                                value={formData.loanParent}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Note
                              </label>
                              <input
                                name="note"
                                value={formData.note}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Control
                              </label>
                              <input
                                name="control"
                                value={formData.control}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Balance
                              </label>
                              <input
                                name="balance"
                                value={formData.balance}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div
                              className="detail-item"
                              style={{ gridColumn: "1 / -1" }}
                            >
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Balance Description
                              </label>
                              <input
                                name="balanceDescription"
                                value={formData.balanceDescription}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Holder
                              </label>
                              <input
                                name="holder"
                                value={formData.holder}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Holder Parent
                              </label>
                              <input
                                name="holderParent"
                                value={formData.holderParent}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Holder CIK
                              </label>
                              <input
                                name="holderCIK"
                                value={formData.holderCIK}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Depositor
                              </label>
                              <input
                                name="depositor"
                                value={formData.depositor}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Depositor CIK
                              </label>
                              <input
                                name="depositorCIK"
                                value={formData.depositorCIK}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Deal
                              </label>
                              <input
                                name="deal"
                                value={formData.deal}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Deal CIK
                              </label>
                              <input
                                name="dealCIK"
                                value={formData.dealCIK}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Accession Number
                              </label>
                              <input
                                name="accessionNumber"
                                value={formData.accessionNumber}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Primary Document
                              </label>
                              <input
                                name="primaryDocument"
                                value={formData.primaryDocument}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Filing Date
                              </label>
                              <input
                                type="date"
                                name="filingDate"
                                value={formData.filingDate}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Document Date
                              </label>
                              <input
                                type="date"
                                name="documentDate"
                                value={formData.documentDate}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>

                            <div className="detail-item">
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "5px",
                                  marginRight: "5px",
                                  fontWeight: "bold",
                                }}
                              >
                                Closing Date
                              </label>
                              <input
                                type="date"
                                name="closingDate"
                                value={formData.closingDate}
                                onChange={handleInputChange}
                                className="form-input"
                                style={{ width: "100%", padding: "8px" }}
                              />
                            </div>
                          </div>
                        </form>
                      ) : (
                        <div className="view-mode">
                          <div className="detail-grid">
                            {/* Read-only View Fields */}
                            <div className="detail-item">
                              <strong>ID: </strong> <span>{report.id}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Loan: </strong> <span>{report.loan}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Loan Parent:</strong>{" "}
                              <span>{report.loanParent}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Note:</strong> <span>{report.note}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Control:</strong>{" "}
                              <span>{report.control}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Balance:</strong>{" "}
                              <span
                                style={{ color: "#2a4bb5", fontWeight: "600" }}
                              >
                                {report.balance}
                              </span>
                            </div>
                            <div
                              className="detail-item"
                              style={{ gridColumn: "1 / -1" }}
                            >
                              <strong>Balance Description:</strong>{" "}
                              <span>{report.balanceDescription}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Holder:</strong>{" "}
                              <span>{report.holder}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Holder Parent:</strong>{" "}
                              <span>{report.holderParent}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Holder CIK:</strong>{" "}
                              <span
                                style={{
                                  fontFamily: "'Monaco', 'Menlo', monospace",
                                  background: "#f8f9fa",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                }}
                              >
                                {report.holderCIK}
                              </span>
                            </div>
                            <div className="detail-item">
                              <strong>Depositor:</strong>{" "}
                              <span>{report.depositor}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Depositor CIK:</strong>{" "}
                              <span
                                style={{
                                  fontFamily: "'Monaco', 'Menlo', monospace",
                                  background: "#f8f9fa",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                }}
                              >
                                {report.depositorCIK}
                              </span>
                            </div>
                            <div className="detail-item">
                              <strong>Deal:</strong> <span>{report.deal}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Deal CIK:</strong>{" "}
                              <span
                                style={{
                                  fontFamily: "'Monaco', 'Menlo', monospace",
                                  background: "#f8f9fa",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                }}
                              >
                                {report.dealCIK}
                              </span>
                            </div>
                            <div className="detail-item">
                              <strong>Accession Number:</strong>{" "}
                              <span
                                style={{
                                  fontFamily: "'Monaco', 'Menlo', monospace",
                                  background: "#f8f9fa",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                }}
                              >
                                {report.accessionNumber}
                              </span>
                            </div>
                            <div className="detail-item">
                              <strong>Primary Document:</strong>{" "}
                              <span>{report.primaryDocument}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Filing Date:</strong>{" "}
                              <span>{formatDateTimeAsStored(report.filingDate)}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Document Date:</strong>{" "}
                              <span>{formatDateTimeAsStored(report.documentDate)}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Closing Date:</strong>{" "}
                              <span>{formatDateTimeAsStored(report.closingDate)}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Acceptance Date:</strong>{" "}
                              <span>{formatDateTimeAsStored(report.acceptanceDate)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Action Buttons */}
                    <div
                      className="action-bar"
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div className="modal-actions">
                        {!isEditingInDetails ? (
                          <>
                            <button
                              className="page-button edit-btn"
                              onClick={toggleEditInDetails}
                            >
                              Edit
                            </button>
                            <button
                              className="page-button delete-btn"
                              onClick={handleDeleteInDetails}
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="page-button"
                              onClick={saveEditInDetails}
                              style={{
                                backgroundColor: "#4CAF50",
                                color: "white",
                              }}
                            >
                              Save
                            </button>
                            <button
                              className="page-button"
                              onClick={toggleEditInDetails}
                              style={{
                                backgroundColor: "#6c757d",
                                color: "white",
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Navigation */}
                    <div
                      className="navigation-bar"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <button
                        className="page-button arrow-button"
                        onClick={handlePrevDetail}
                        disabled={currentDetailIndex === 0}
                        style={{
                          opacity: currentDetailIndex === 0 ? 0.5 : 1,
                          backgroundColor:
                            currentDetailIndex === 0 ? "#cccccc" : "#152c70",
                        }}
                      >
                        ← Previous
                      </button>
                      <div className="nav-indicator">
                        <span className="nav-text">
                          Entry {currentDetailIndex + 1} of {sortedData.length}
                        </span>
                      </div>
                      <button
                        className="page-button arrow-button"
                        onClick={handleNextDetail}
                        disabled={currentDetailIndex >= sortedData.length - 1}
                        style={{
                          opacity:
                            currentDetailIndex >= sortedData.length - 1
                              ? 0.5
                              : 1,
                          backgroundColor:
                            currentDetailIndex >= sortedData.length - 1
                              ? "#cccccc"
                              : "#152c70",
                        }}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* CRITICAL: Edit Uploaded Row Modal - RENDERED BEFORE Upload Modal */}
      {showEditModal && showUploadModal && (
        <div className="modal-overlay" style={{ zIndex: 1001 }}>
          <div className="modal-content rf-modal">
            <button
              className="close-button"
              onClick={() => { setShowEditModal(false); resetForm(); }}
            >
              ×
            </button>
            <h3>Edit uploaded row</h3>
            <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
              Adjust fields before the row is committed to the report.
            </p>
            <ReportForm
              formData={formData}
              handleInputChange={handleInputChange}
              onSubmit={updateUploadedRow}
              onCancel={() => { setShowEditModal(false); resetForm(); }}
              loading={loading}
              buttonText="Update row"
            />
          </div>
        </div>
      )}

      {/* Upload Preview Modal - RENDERED AFTER Edit Modal */}
      {showUploadModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1000 }}
        >
          <div
            className="modal-content"
            style={{ width: "90%", maxWidth: "1200px" }}
          >
            <button
              className="close-button"
              onClick={() => {
                setShowUploadModal(false);
                setUploadedData([]);
              }}
            >
              ×
            </button>
            <h3>Upload Preview - {uploadedData.length} records found</h3>
            <p>
              Review the data below. You can edit or delete individual rows
              before submitting.
            </p>

            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                marginBottom: "20px",
              }}
            >
              <table className="note-table">
                <thead>
                  <tr>
                    <th>Loan</th>
                    <th>Holder</th>
                    <th>Note</th>
                    <th>Control</th>
                    <th>Balance Description</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.loan}</td>
                      <td>{row.holder}</td>
                      <td>{row.note}</td>
                      <td>{row.control}</td>
                      <td>{row.balanceDescription}</td>
                      <td>{row.balance}</td>
                      <td style={{ display: "flex", gap: "8px" }}>
                        <div
                          onClick={() => editUploadedRow(index)}
                          style={{ cursor: "pointer" }}
                        >
                          <EditIcon />
                        </div>
                        <div
                          onClick={() => deleteUploadedRow(index)}
                          style={{ cursor: "pointer" }}
                        >
                          <DeleteIcon />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="page-button"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedData([]);
                }}
              >
                Cancel
              </button>
              <button
                className="page-button"
                onClick={submitUploadedData}
                disabled={loading || uploadedData.length === 0}
              >
                {loading
                  ? "Uploading..."
                  : `Submit ${uploadedData.length} Records`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regular Edit Report Modal (for main table) */}
      {showEditModal && !showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="close-button"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
            >
              ×
            </button>
            <h3>Edit Excel Report</h3>
            <ReportForm
              formData={formData}
              handleInputChange={handleInputChange}
              onSubmit={updateReport}
              loading={loading}
              buttonText="Update Report"
            />
          </div>
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content rf-modal">
            <button
              className="close-button"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
            >
              ×
            </button>
            <h3>Create new Excel report</h3>
            <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
              Enter the loan, holder, depositor, deal and filing details for this report.
            </p>
            <ReportForm
              formData={formData}
              handleInputChange={handleInputChange}
              onSubmit={createReport}
              onCancel={() => { setShowCreateModal(false); resetForm(); }}
              loading={loading}
              buttonText="Create report"
            />
          </div>
        </div>
      )}

      {/* Upload Preview Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{
              width: "90%",
              maxWidth: "1200px",
              backgroundColor: "white",
            }}
          >
            <button
              className="close-button"
              onClick={() => {
                setShowUploadModal(false);
                setUploadedData([]);
              }}
            >
              ×
            </button>
            <h3>Upload Preview - {uploadedData.length} records found</h3>
            <p>
              Review the data below. You can edit or delete individual rows
              before submitting.
            </p>

            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                marginBottom: "20px",
              }}
            >
              <table className="note-table">
                <thead>
                  <tr>
                    <th>Loan</th>
                    <th>Holder</th>
                    <th>Note</th>
                    <th>Control</th>
                    <th>Balance Description</th>
                    <th>Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.loan}</td>
                      <td>{row.holder}</td>
                      <td>{row.note}</td>
                      <td>{row.control}</td>
                      <td>{row.balanceDescription}</td>
                      <td>{row.balance}</td>
                      <td style={{ display: "flex", gap: "8px" }}>
                        <div
                          onClick={() => editUploadedRow(index)}
                          style={{ cursor: "pointer" }}
                        >
                          <EditIcon />
                        </div>
                        <div
                          onClick={() => deleteUploadedRow(index)}
                          style={{ cursor: "pointer" }}
                        >
                          <DeleteIcon />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="page-button"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedData([]);
                }}
              >
                Cancel
              </button>
              <button
                className="page-button"
                onClick={submitUploadedData}
                disabled={loading || uploadedData.length === 0}
              >
                {loading
                  ? "Uploading..."
                  : `Submit ${uploadedData.length} Records`}
              </button>
            </div>

            {/* EDIT MODAL INSIDE UPLOAD MODAL */}
            {showEditModal && (
              <div className="modal-overlay" style={{ zIndex: 1001 }}>
                <div className="modal-content rf-modal">
                  <button
                    className="close-button"
                    onClick={() => { setShowEditModal(false); resetForm(); }}
                  >
                    ×
                  </button>
                  <h3>Edit uploaded row</h3>
                  <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
                    Adjust fields before the row is committed to the report.
                  </p>
                  <ReportForm
                    formData={formData}
                    handleInputChange={handleInputChange}
                    onSubmit={updateUploadedRow}
                    onCancel={() => { setShowEditModal(false); resetForm(); }}
                    loading={loading}
                    buttonText="Update row"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {showViewModal && selectedReport && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "700px" }}>
            <button
              className="close-button"
              onClick={() => {
                setShowViewModal(false);
                setSelectedReport(null);
              }}
            >
              ×
            </button>
            <h3>View Excel Report - ID: {selectedReport.id}</h3>
            <div className="report-details-view">
              <div className="detail-section">
                <h4>Loan Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Loan:</strong> {selectedReport.loan}
                  </div>
                  <div className="detail-item">
                    <strong>Loan Parent:</strong>{" "}
                    {selectedReport.loanParent || "N/A"}
                  </div>
                  <div className="detail-item">
                    <strong>Note:</strong> {selectedReport.note || "N/A"}
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Financial Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Control:</strong> {selectedReport.control || "N/A"}
                  </div>
                  <div className="detail-item">
                    <strong>Balance:</strong> {selectedReport.balance || "0"}
                  </div>
                  <div className="detail-item">
                    <strong>Balance Description:</strong>{" "}
                    {selectedReport.balanceDescription || "N/A"}
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Holder Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Holder:</strong> {selectedReport.holder}
                  </div>
                  <div className="detail-item">
                    <strong>Holder Parent:</strong>{" "}
                    {selectedReport.holderParent || "N/A"}
                  </div>
                  <div className="detail-item">
                    <strong>Holder CIK:</strong>{" "}
                    {selectedReport.holderCIK || "N/A"}
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Depositor Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Depositor:</strong> {selectedReport.depositor}
                  </div>
                  <div className="detail-item">
                    <strong>Depositor CIK:</strong>{" "}
                    {selectedReport.depositorCIK || "N/A"}
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Deal Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Deal:</strong> {selectedReport.deal}
                  </div>
                  <div className="detail-item">
                    <strong>Deal CIK:</strong> {selectedReport.dealCIK || "N/A"}
                  </div>
                  <div className="detail-item">
                    <strong>Accession Number:</strong>{" "}
                    {selectedReport.accessionNumber || "N/A"}
                  </div>
                  <div className="detail-item">
                    <strong>Primary Document:</strong>{" "}
                    {selectedReport.primaryDocument || "N/A"}
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Date Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Filing Date:</strong>{" "}
                    {formatDateTimeAsStored(selectedReport.filingDate)}
                  </div>
                  <div className="detail-item">
                    <strong>Document Date:</strong>{" "}
                    {formatDateTimeAsStored(selectedReport.documentDate)}
                  </div>
                  <div className="detail-item">
                    <strong>Closing Date:</strong>{" "}
                    {formatDateTimeAsStored(selectedReport.closingDate)}
                  </div>
                  <div className="detail-item">
                    <strong>Created:</strong>{" "}
                    {formatDateTimeAsStored(selectedReport.acceptanceDate)}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <button
                className="page-button"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedReport(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable form component
const ReportForm = ({
  formData,
  handleInputChange,
  onSubmit,
  loading,
  buttonText,
  onCancel,
}) => {
  // Fields are grouped so the form reads top-to-bottom in logical sections.
  const FIELDS = [
    { group: "Loan & note", items: [
      { name: "loan",                label: "Loan",                required: true,  placeholder: "Loan name" },
      { name: "loanParent",          label: "Loan parent",         placeholder: "Optional parent loan" },
      { name: "note",                label: "Note",                placeholder: "Note name" },
      { name: "control",             label: "Control",             type: "select", options: ["", "Control", "Non-Control", "Subordinate"] },
      { name: "balance",             label: "Balance",             type: "number", step: "0.01", placeholder: "0.00" },
      { name: "balanceDescription",  label: "Balance description", placeholder: "Optional description" },
    ]},
    { group: "Holder", items: [
      { name: "holder",       label: "Holder",        placeholder: "Holder name" },
      { name: "holderParent", label: "Holder parent", placeholder: "Optional parent holder" },
      { name: "holderCIK",    label: "Holder CIK",    placeholder: "SEC CIK (digits)" },
    ]},
    { group: "Depositor & deal", items: [
      { name: "depositor",       label: "Depositor",     placeholder: "Depositor name" },
      { name: "depositorCIK",    label: "Depositor CIK", placeholder: "SEC CIK (digits)" },
      { name: "deal",            label: "Deal",          placeholder: "Deal name" },
      { name: "dealCIK",         label: "Deal CIK",      placeholder: "SEC CIK (digits)" },
    ]},
    { group: "Filing details", items: [
      { name: "accessionNumber", label: "Accession number", placeholder: "0000000000-00-000000" },
      { name: "primaryDocument", label: "Primary document", placeholder: "filename.htm" },
      { name: "filingDate",      label: "Filing date",      type: "date" },
      { name: "documentDate",    label: "Document date",    type: "date" },
      { name: "closingDate",     label: "Closing date",     type: "date" },
      { name: "acceptanceDate",  label: "Acceptance date",  type: "datetime-local" },
    ]},
  ];

  return (
    <form onSubmit={onSubmit} className="rf-form">
      {FIELDS.map((section) => (
        <fieldset key={section.group} className="rf-section">
          <legend className="rf-legend">{section.group}</legend>
          <div className="note-form">
            {section.items.map((f) => (
              <div key={f.name} className="form-group">
                <label htmlFor={`rf-${f.name}`}>{f.label}{f.required && " *"}</label>
                {f.type === "select" ? (
                  <select
                    id={`rf-${f.name}`}
                    name={f.name}
                    value={formData[f.name] ?? ""}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    {(f.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt || `Select ${f.label.toLowerCase()}`}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`rf-${f.name}`}
                    type={f.type || "text"}
                    name={f.name}
                    value={formData[f.name] ?? ""}
                    onChange={handleInputChange}
                    step={f.step}
                    placeholder={f.placeholder}
                    required={!!f.required}
                    className="form-input"
                  />
                )}
              </div>
            ))}
          </div>
        </fieldset>
      ))}

      <div className="mn-form-actions">
        {onCancel && (
          <button type="button" className="btn btn-ghost-themed" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn" disabled={loading}>
          {loading ? (
            <><i className="bi bi-arrow-repeat" style={{ animation: "spin 0.7s linear infinite" }} /> Processing…</>
          ) : (
            <><i className="bi bi-save" /> {buttonText}</>
          )}
        </button>
      </div>
    </form>
  );
};

export default ExcelReports;
